import React, { useEffect, useState, lazy } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { CTABanner } from '../components/CTABanner';
export function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        serviceType: '',
        message: ''
      });
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }, 1500);
  };
  return <div className="w-full" data-id="element-302">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-navy" data-id="element-303">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1200')] bg-cover bg-center opacity-20" data-id="element-304"></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-305">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-6" data-id="element-306">
            Get a Free Quote
          </h1>
          <p className="text-xl text-cream max-w-3xl mx-auto" data-id="element-307">
            Ready to restore your property's beauty? Contact us today for a
            fast, free, no-obligation estimate.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-308">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-309">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-310">
            {/* Contact Form */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-100" data-id="element-311">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-6" data-id="element-312">
                Send Us a Message
              </h2>

              {submitStatus === 'success' && <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200 flex items-center" data-id="element-313">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2" data-id="element-314"></div>
                  Thank you! Your message has been sent. We'll be in touch
                  shortly.
                </div>}

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-315">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-316">
                  <div data-id="element-317">
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-318">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-light-blue focus:border-light-blue transition-colors outline-none" placeholder="John Doe" data-id="element-319" />
                  </div>
                  <div data-id="element-320">
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-321">
                      Phone Number *
                    </label>
                    <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-light-blue focus:border-light-blue transition-colors outline-none" placeholder="586-298-2727" data-id="element-322" />
                  </div>
                </div>

                <div data-id="element-323">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-324">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-light-blue focus:border-light-blue transition-colors outline-none" placeholder="john@example.com" data-id="element-325" />
                </div>

                <div data-id="element-326">
                  <label htmlFor="serviceType" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-327">
                    Service Needed
                  </label>
                  <select id="serviceType" name="serviceType" value={formData.serviceType} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-light-blue focus:border-light-blue transition-colors outline-none bg-white" data-id="element-328">
                    <option value="" data-id="element-329">Select a service...</option>
                    <option value="Residential" data-id="element-330">
                      Residential Soft Washing
                    </option>
                    <option value="Roof" data-id="element-331">Roof Cleaning</option>
                    <option value="Gutter" data-id="element-332">Gutter Cleaning</option>
                    <option value="Driveway" data-id="element-333">Driveway & Concrete</option>
                    <option value="Deck" data-id="element-334">Deck & Patio</option>
                    <option value="Fence" data-id="element-335">Fence Cleaning</option>
                    <option value="Commercial" data-id="element-336">Commercial Services</option>
                    <option value="Other" data-id="element-337">Other / Multiple</option>
                  </select>
                </div>

                <div data-id="element-338">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-339">
                    How can we help? *
                  </label>
                  <textarea id="message" name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-light-blue focus:border-light-blue transition-colors outline-none resize-none" placeholder="Tell us about your property and what needs cleaning..." data-id="element-340"></textarea>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-navy text-white font-bold py-4 px-8 rounded-lg hover:bg-light-blue transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-341">
                  {isSubmitting ? <span className="flex items-center" data-id="element-342">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-343">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-344"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-345"></path>
                      </svg>
                      Sending...
                    </span> : <span className="flex items-center" data-id="element-346">
                      <Send className="w-5 h-5 mr-2" data-id="element-347" />
                      Get My Free Quote
                    </span>}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8" data-id="element-348">
              <div className="bg-navy text-white p-8 rounded-2xl shadow-xl relative overflow-hidden" data-id="element-349">
                <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-light-blue opacity-20 rounded-full blur-2xl" data-id="element-350"></div>
                <h3 className="text-2xl font-montserrat font-bold mb-8 relative z-10" data-id="element-351">
                  Contact Information
                </h3>

                <div className="space-y-6 relative z-10" data-id="element-352">
                  <div className="flex items-start" data-id="element-353">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center shrink-0 mr-4" data-id="element-354">
                      <Phone className="w-6 h-6 text-light-blue" data-id="element-355" />
                    </div>
                    <div data-id="element-356">
                      <p className="text-sm text-cream mb-1" data-id="element-357">Call or Text Us</p>
                      <a href="tel:586-298-2727" className="text-xl font-bold hover:text-light-blue transition-colors" data-id="element-358">
                        586-298-2727
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-359">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center shrink-0 mr-4" data-id="element-360">
                      <Mail className="w-6 h-6 text-light-blue" data-id="element-361" />
                    </div>
                    <div data-id="element-362">
                      <p className="text-sm text-cream mb-1" data-id="element-363">Email Us</p>
                      <a href="mailto:SoftwashPros@gmail.com" className="text-lg font-bold hover:text-light-blue transition-colors break-all" data-id="element-364">
                        SoftwashPros@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-365">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center shrink-0 mr-4" data-id="element-366">
                      <Clock className="w-6 h-6 text-light-blue" data-id="element-367" />
                    </div>
                    <div data-id="element-368">
                      <p className="text-sm text-cream mb-1" data-id="element-369">Business Hours</p>
                      <p className="font-bold" data-id="element-370">Mon - Sat: 7:00 AM - 7:00 PM</p>
                      <p className="text-cream text-sm" data-id="element-371">
                        Sunday: By Appointment
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-gray-200 rounded-2xl h-64 overflow-hidden shadow-inner border border-gray-300" data-id="element-372">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d377194.4741488056!2d-83.56847055000001!3d42.59850125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8824ca0110cb1d75%3A0x5776864e35b9c4d2!2sDetroit%20Metropolitan%20Area%2C%20MI!5e0!3m2!1sen!2sus!4v1715000000000!5m2!1sen!2sus" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Service Area Map - Detroit Metropolitan Area" data-id="element-373"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}